import React, { useState, useEffect, useContext } from 'react';
import { Context } from '../context';
import * as FC from '@fluentui/react-components';

const STATUS_ORDER = ['create', 'check', 'annotation', 'recheck', 'completed', 'ready_to_release', 'released'];
const STATUS_LABELS = {
    create: '待上传',
    check: '待检查',
    annotation: '标注中',
    recheck: '复查中',
    completed: '已完成',
    ready_to_release: '待发布',
    released: '已发布',
    unknown: '未知',
};
const STATUS_STYLES = {
    create:    { badge: '#FF4D4F', card: '#FFE5E5', text: '#8C1A1C', border: '#FFB3B4' },
    check:     { badge: '#1677FF', card: '#E3F2FF', text: '#0B3D91', border: '#99C8FF' },
    annotation:{ badge: '#FA8C16', card: '#FFF1DA', text: '#A05A05', border: '#FFD19B' },
    recheck:   { badge: '#722ED1', card: '#F2E8FF', text: '#45208B', border: '#C8A8FF' },
    completed: { badge: '#389E0D', card: '#E6F7E6', text: '#1D5D09', border: '#A8E6A1' },
    ready_to_release: { badge: '#13C2C2', card: '#E6FFFB', text: '#006D75', border: '#87E8DE' },
    released:  { badge: '#595959', card: '#F5F5F5', text: '#262626', border: '#D9D9D9' },
    unknown:   { badge: '#8C8C8C', card: '#F5F5F5', text: '#555555', border: '#DDDDDD' },
};

const normalizeFlowStatus = (status) => {
    if (!status) return status;
    const v = String(status).toLowerCase();
    if (v === 'created') return 'create';
    if (v === 'checking') return 'check';
    if (v === 'rechecking') return 'recheck';
    if (v === 'ready_to_release' || v === 'readytorelease') return 'ready_to_release';
    if (v === 'released') return 'released';
    return v;
};

const getStatusStyle = (status) => STATUS_STYLES[status] || STATUS_STYLES.unknown;
const getStatusText = (status) => STATUS_LABELS[status] || STATUS_LABELS.unknown;

const StatusBadge = ({ status }) => {
    const style = getStatusStyle(status);

    return (
        <span
            style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: style.badge,
            }}
        />
    );
};

const StatusFlow = ({ currentStatus, onStatusClick }) => {
    const normalized = normalizeFlowStatus(currentStatus);

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                maxWidth: 1100,
                gap: 12,
                padding: '12px 0',
            }}
        >
            {STATUS_ORDER.map((status, index) => (
                <React.Fragment key={status}>
                    <FC.Button
                        appearance={normalized === status ? 'primary' : 'outline'}
                        size="medium"
                        onClick={() => onStatusClick?.(status)}
                        style={{
                            minWidth: 120,
                            height: 48,
                            borderRadius: 6,
                            fontSize: 14,
                            fontWeight: normalized === status ? 600 : 500,
                        }}
                    >
                        {getStatusText(status)}
                    </FC.Button>
                    {index < STATUS_ORDER.length - 1 && (
                        <span style={{ color: '#bbb', fontSize: 16 }}>→</span>
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

export const TurbineTaskStatusView = ({ task, session, onStatusChange, taskInfo }) => {
    const { apiClient } = useContext(Context);
    const [selectedTurbineId, setSelectedTurbineId] = useState(null);
    const [turbineStatuses, setTurbineStatuses] = useState({});
    const [selectedTurbineData, setSelectedTurbineData] = useState(null);

    useEffect(() => {
        if (task?.turbines?.length > 0 && !selectedTurbineId) {
            const firstTurbine = [...task.turbines].sort((a, b) => a.id - b.id)[0];
            setSelectedTurbineId(firstTurbine.id);
        }
    }, [task, selectedTurbineId]);

    const deriveTurbineStatus = (turbine) => {
        if (!turbine) return 'create';

        // Check for review states first (from session or turbine)
        if (session?.ReviewStatus === 'released' || turbine.review_status === 'released') return 'released';
        if (session?.ReviewStatus === 'ready_to_release' || turbine.review_status === 'ready_to_release') return 'ready_to_release';

        const normalized = normalizeFlowStatus(turbine.annotation_status);
        if (normalized) return normalized;
        if (turbine.completed) return 'completed';
        if (turbine.ready_for_annotation) return 'check';
        return 'create';
    };

    useEffect(() => {
        if (selectedTurbineId && task) {
            const turbine = task.turbines?.find((t) => t.id === selectedTurbineId);
            if (turbine) {
                const derivedStatus =
                    turbineStatuses[selectedTurbineId] ||
                    deriveTurbineStatus(turbine) ||
                    normalizeFlowStatus(task.status) ||
                    'check';

                setSelectedTurbineData({
                    ...task,
                    turbine,
                    status: derivedStatus,
                });
            }
        }
    }, [selectedTurbineId, task, turbineStatuses]);

    const handleTurbineSelect = (turbineId) => setSelectedTurbineId(turbineId);

    const handleStatusChange = async (newStatus) => {
        if (!task || !selectedTurbineId) return;

        try {
            const turbine = task.turbines?.find((x) => x.id === selectedTurbineId);
            const targetTaskId = turbine?.annotation_task_id || task.id;

            await apiClient.updateTask(targetTaskId, { Status: newStatus });

            setTurbineStatuses((prev) => ({
                ...prev,
                [selectedTurbineId]: newStatus,
            }));

            setSelectedTurbineData((prev) => ({
                ...prev,
                status: newStatus,
            }));

            onStatusChange?.(selectedTurbineId, newStatus);
        } catch (error) {
            console.error('Failed to update turbine status:', error);
            alert('Failed to update status: ' + (error.message || 'Unknown error'));
        }
    };

    const legend = (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
            {STATUS_ORDER.map((status) => {
                const style = getStatusStyle(status);
                return (
                    <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                            style={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                backgroundColor: style.badge,
                                border: `2px solid ${style.badge}`,
                                display: 'inline-block',
                            }}
                        />
                        <FC.Text size={200}>{getStatusText(status)}</FC.Text>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div style={{ display: 'flex', gap: 20, height: '100%', padding: 20 }}>
            <div style={{ flex: '0 0 350px', display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ marginTop: 0, marginBottom: 10 }}>风机列表</h3>
                {legend}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        padding: 10,
                        backgroundColor: FC.tokens.colorNeutralBackground2,
                        borderRadius: 6,
                        flex: 1,
                        overflowY: 'auto',
                    }}
                >
                    {task?.turbines?.map((turbine) => {
                        const status = deriveTurbineStatus(turbine);
                        const style = getStatusStyle(status);
                        const isSelected = selectedTurbineId === turbine.id;

                        return (
                            <div
                                key={turbine.id}
                                onClick={() => handleTurbineSelect(turbine.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: 12,
                                    backgroundColor: isSelected
                                        ? FC.tokens.colorBrandBackground
                                        : style.card,
                                    color: isSelected ? 'white' : style.text,
                                    borderRadius: 8,
                                    border: isSelected
                                        ? `1px solid ${FC.tokens.colorBrandForeground2}`
                                        : `1px solid ${style.border}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                            >
                                <StatusBadge status={turbineStatuses[turbine.id] || status} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <div style={{ fontWeight: 600 }}>
                                        {turbine.name || `风机 ${turbine.id}`}
                                    </div>
                                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                                        {getStatusText(turbineStatuses[turbine.id] || status)}
                                    </div>
                                </div>
                                <div style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.7 }}>
                                    {turbine.serial_number || turbine.code || ''}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {selectedTurbineId && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{ marginTop: 0, marginBottom: 10 }}>
                        作业状态 - 当前风机:{' '}
                        {task?.turbines?.find((t) => t.id === selectedTurbineId)?.name || selectedTurbineId}
                    </h3>
                    <div
                        style={{
                            padding: '16px 20px',
                            backgroundColor: 'white',
                            borderRadius: 8,
                            boxShadow: FC.tokens.shadow4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 260,
                        }}
                    >
                        {selectedTurbineData ? (
                            <>
                                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <FC.Text size={200}>当前状态：</FC.Text>
                                    <FC.Text weight="semibold">{getStatusText(selectedTurbineData.status)}</FC.Text>
                                </div>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(4, 1fr)',
                                        gap: 12,
                                        width: '100%',
                                        maxWidth: 900,
                                        marginBottom: 12,
                                    }}
                                >
                                    <div style={{ textAlign: 'center' }}>
                                        <FC.Text size={200}>创建人</FC.Text>
                                        <div>{taskInfo?.owner_creater_name || task?.owner_creater?.name || '-'}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <FC.Text size={200}>巡检人员</FC.Text>
                                        <div>{taskInfo?.owner_checker_name || task?.owner_checker?.name || '-'}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <FC.Text size={200}>标注人员</FC.Text>
                                        <div>{taskInfo?.owner_annotator_name || task?.owner_annotator?.name || '-'}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <FC.Text size={200}>复核人员</FC.Text>
                                        <div>{taskInfo?.owner_rechecker_name || task?.owner_rechecker?.name || '-'}</div>
                                    </div>
                                </div>
                                <StatusFlow
                                    currentStatus={selectedTurbineData.status}
                                    onStatusClick={handleStatusChange}
                                />
                            </>
                        ) : (
                            <FC.Text>选择风机以查看详情</FC.Text>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
